import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  IconButton,
  TextField,
  Button,
  InputAdornment,
  Tooltip,
  Snackbar,
  Alert as MuiAlert,
  CircularProgress,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useNavigate, useSearchParams } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';

import PageWrapper from 'components/Common/PageWraper';
import { CustomizedDataGrid } from 'components/customized_data_grid';
import DeleteConfirmationModal from 'components/Modal/DeleteConfirmationModal';
import { getSopList, getSopMetrics, createSop, updateSop, deleteSop, approveSop, rejectSop } from 'service/unifiedAlerts';
import { getDecodedToken } from 'service/service';
import NumberWidget from 'components/Widgets/NumberWidget';
import { renderTooltipIcon } from 'components/TableauObs/common/KpiCard';
import AddEditSopModal from './AddEditSopModal';
import ApproveRejectSopModal from './ApproveRejectSopModal';
import PepwiseToast from 'components/Widgets/PepwiseToast';
import moment from 'moment';

// ─── Types ────────────────────────────────────────────────────────────────────
interface SopRow {
  id: string;
  name: string;
  sopName?: string;
  link?: string;
  version?: number;
  updatedBy?: string;
  updatedByEmail?: string;
  updatedAt?: string;
  lastUpdated?: string;
  createdBy?: string;
  createdByEmail?: string;
  status?: string;
  owner?: string;
  assignedApprover?: string;
  approvalRequired?: boolean;
  comments?: string;
  type?: string;
}

const KPI_STATUS_MAP: Record<string, string | undefined> = {
  'Total SOPs': undefined,
  'Approval Pending': 'Pending Approval',
  'Rejected': 'Rejected',
  'Approved': 'Approved',
};

// ─── Column width helper ───────────────────────────────────────────────────────
const autoColumnWidth = (min = 140, flex = 1, max = min * 1.5) =>
  `minmax(clamp(${min}px, ${min / 10}vw, ${max}px), ${flex}fr)`;

const formatLastUpdated = (date?: string | Date | null) => {
  if (!date) return '-';
  const m = moment(date);
  return m.isValid() ? m.format('MM-DD-YYYY HH:mm') : '-';
};


// ─── Page ─────────────────────────────────────────────────────────────────────
const ManageSOPPage: React.FC = () => {
  const theme = useTheme();
  const colors = theme.palette;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const typeParam = searchParams.get('type');
  const sopType = typeParam || 'EWS';

  useEffect(() => {
    if (!typeParam) {
      navigate('/dataops/manage-sop?type=EWS', { replace: true });
    }
  }, [typeParam, navigate]);

  const dashboardRoute = sopType === 'OPS_PULSE' ? '/dataops/unified-incidents' : '/dataops/unified-alerts';
  const handleBack = () => {
    if (window.history.state && window.history.state.idx > 0) {
      navigate(-1);
    } else {
      navigate(dashboardRoute);
    }
  };

  // Shared input sx
  const inputSx = {
    '& .MuiOutlinedInput-root': {
      bgcolor: colors.inputNew.inputBackground,
      height: '40px',
      fontSize: '0.85rem',
      color: `${colors.inputNew.inputText} !important`,
      '& fieldset': { borderColor: `${colors.inputNew.inputBorder} !important` },
      '&:hover fieldset': { borderColor: `${colors.inputNew.inputBorderHover} !important` },
      '&.Mui-focused fieldset': { borderColor: `${colors.inputNew.inputBorderFocus} !important` },
    },
    '& .MuiOutlinedInput-input': {
      color: colors.inputNew.inputText,
      WebkitTextFillColor: colors.inputNew.inputText,
      caretColor: colors.inputNew.inputText,
    },
    '& .MuiInputBase-input::placeholder': {
      color: `${colors.inputNew.inputPlaceholder} !important`,
      opacity: '1 !important',
    },
  };


  // ── List state ─────────────────────────────────────────────────────────────
  const [sops, setSops] = useState<SopRow[]>([]);
  const [totalDocs, setTotalDocs] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sortingInfo, setSortingInfo] = useState({ sortBy: '', sortDir: '' });
  const [currFilter, setCurrFilter] = useState<Record<string, any>>({});
  const [search, setSearch] = useState('');
  const [searchTouched, setSearchTouched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeKpi, setActiveKpi] = useState<string>('Total SOPs');
  const activeKpiRef = useRef(activeKpi);
  activeKpiRef.current = activeKpi;
  const [metrics, setMetrics] = useState({
    totalSOPs: 0,
    pendingApproval: 0,
    rejected: 0,
    approved: 0,
  });
  const [metricsLoading, setMetricsLoading] = useState(false);

  // ── Modal state ────────────────────────────────────────────────────────────
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [isResubmit, setIsResubmit] = useState(false);
  const [sopName, setSopName] = useState('');
  const [sopLink, setSopLink] = useState('');
  const [sopApprover, setSopApprover] = useState('');
  const [sopApprovalRequired, setSopApprovalRequired] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  // ── Delete state ───────────────────────────────────────────────────────────
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [sopToDelete, setSopToDelete] = useState<SopRow | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  // ── Approve / Reject modal state ───────────────────────────────────────────
  const [approvalModalState, setApprovalModalState] = useState<{
    open: boolean;
    type: 'approve' | 'reject';
    sop: SopRow | null;
  }>({
    open: false,
    type: 'approve',
    sop: null,
  });
  const [approvalLoading, setApprovalLoading] = useState(false);

  const currentUserEmail = useMemo(() => {
    try {
      const token = getDecodedToken();
      return (token?.email || '').toLowerCase().trim();
    } catch {
      return '';
    }
  }, []);

  const currentUserId = useMemo(() => {
    try {
      const token = getDecodedToken();
      return String(token?.userId || token?._id || token?.id || '').trim();
    } catch {
      return '';
    }
  }, []);

  const isCreatorOfSop = useCallback((row: SopRow) => {
    try {
      const token = getDecodedToken();
      const userEmail = (token?.email || currentUserEmail || '').toLowerCase().trim();
      const userId = String(token?.userId || token?._id || token?.id || currentUserId || '').trim();

      const creatorEmail = (row.createdByEmail || '').toLowerCase().trim();
      const creatorId = String(row.createdBy || '').trim();

      return Boolean(
        (userEmail && creatorEmail && creatorEmail === userEmail) ||
        (userEmail && creatorId && creatorId.toLowerCase() === userEmail) ||
        (userId && creatorId && creatorId === userId)
      );
    } catch {
      return false;
    }
  }, [currentUserEmail, currentUserId]);

  const isApproverOfSop = useCallback((row: SopRow) => {
    try {
      const token = getDecodedToken();
      const userEmail = (token?.email || currentUserEmail || '').toLowerCase().trim();
      const userId = String(token?.userId || token?._id || token?.id || currentUserId || '').trim();

      const approver = (row.assignedApprover || '').toLowerCase().trim();
      if (!approver) return false;

      return Boolean(
        (userEmail && approver === userEmail) ||
        (userId && approver === userId)
      );
    } catch {
      return false;
    }
  }, [currentUserEmail, currentUserId]);

  const canDeleteSop = useCallback((row: SopRow) => {
    const isApproved = /approved/i.test(row.status || '');
    const isCreator = isCreatorOfSop(row);
    const approver = (row.assignedApprover || '').trim();

    return isApproved ? (approver ? isApproverOfSop(row) : isCreator) : isCreator;
  }, [isCreatorOfSop, isApproverOfSop]);

  // ── Snackbar ───────────────────────────────────────────────────────────────
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    title: string;
    message: string;
    severity: 'success' | 'error' | 'alert';
  }>({
    open: false,
    title: '',
    message: '',
    severity: 'success',
  });

  const showSnack = (
    messageOrTitle: string,
    severity: 'success' | 'error' | 'alert' = 'success',
    subMessage?: string
  ) => {
    let title = messageOrTitle;
    let message = subMessage || '';

    if (!subMessage) {
      if (severity === 'error') {
        title = /failed to delete/i.test(messageOrTitle)
          ? 'Failed to Delete SOP.'
          : /failed to/i.test(messageOrTitle)
          ? (messageOrTitle.endsWith('.') ? messageOrTitle : `${messageOrTitle}.`)
          : 'Operation Failed.';
        message = messageOrTitle;
      } else if (severity === 'alert') {
        title = 'Alert';
        message = messageOrTitle;
      } else {
        if (/deleted/i.test(messageOrTitle)) {
          title = 'SOP Deleted Successfully.';
          message = 'SOP has been deleted successfully.';
        } else if (/created/i.test(messageOrTitle)) {
          title = 'SOP Created Successfully.';
          message = 'SOP has been created successfully.';
        } else if (/updated/i.test(messageOrTitle)) {
          title = 'SOP Updated Successfully.';
          message = 'SOP has been updated successfully.';
        } else if (/resubmitted/i.test(messageOrTitle)) {
          title = 'SOP Resubmitted Successfully.';
          message = messageOrTitle;
        } else if (/approved/i.test(messageOrTitle)) {
          title = 'SOP Approved Successfully.';
          message = messageOrTitle;
        } else if (/rejected/i.test(messageOrTitle)) {
          title = 'SOP Rejected.';
          message = messageOrTitle;
        } else {
          title = messageOrTitle.endsWith('.') ? messageOrTitle : `${messageOrTitle}.`;
          message = messageOrTitle;
        }
      }
    }

    setSnackbar({ open: true, title, message, severity });
  };

  // ── Fetch Metrics ──────────────────────────────────────────────────────────
  const fetchMetrics = useCallback(async () => {
    setMetricsLoading(true);
    try {
      const res = await getSopMetrics(sopType);
      if (res?.success === true && res?.data) {
        setMetrics({
          totalSOPs: res.data.totalSOPs ?? 0,
          pendingApproval: res.data.pendingApproval ?? 0,
          rejected: res.data.rejected ?? 0,
          approved: res.data.approved ?? 0,
        });
      }
    } catch (err) {
      console.error('Error fetching SOP metrics:', err);
    } finally {
      setMetricsLoading(false);
    }
  }, [sopType]);

  // ── Fetch SOPs ─────────────────────────────────────────────────────────────
  const fetchSops = useCallback(async (
    searchVal: string,
    currentPage: number,
    currentPageSize: number,
    currentSorting: { sortBy: string; sortDir: string },
    currentKpi?: string
  ) => {
    setLoading(true);
    try {
      const kpiToUse = currentKpi !== undefined ? currentKpi : activeKpiRef.current;
      const statusFilter = KPI_STATUS_MAP[kpiToUse];
      const res = await getSopList(sopType, currentPage, currentPageSize, currentSorting, searchVal, statusFilter);
      const data = res?.data;
      setSops((data?.docs ?? []).map((s: any) => ({
        id: s._id,
        name: s.sopName,
        sopName: s.sopName,
        link: s.sopLink,
        version: s.version,
        updatedBy: s.updatedBy,
        updatedByEmail: s.updatedByEmail,
        updatedAt: s.updatedAt,
        lastUpdated: formatLastUpdated(s.updatedAt),
        createdBy: s.createdBy,
        createdByEmail: s.createdByEmail,
        status: s.status,
        owner: s.assignedApprover,
        assignedApprover: s.assignedApprover,
        approvalRequired: s.approvalRequired,
        comments: s.comments,
        type: s.type,
      })));
      setTotalDocs(data?.totalDocs ?? 0);
      setTotalPages(data?.totalPages ?? 1);
      setPage(data?.page ?? 1);
      setPageSize(data?.limit ?? 10);
    } catch {
      setSops([]);
      setTotalDocs(0);
    } finally {
      setLoading(false);
    }
  }, [sopType]);

  const handleKpiClick = (kpiKey: string) => {
    setActiveKpi(kpiKey);
    activeKpiRef.current = kpiKey;
    setPage(1);
    fetchSops(search.trim(), 1, pageSize, sortingInfo, kpiKey);
  };

  useEffect(() => {
    activeKpiRef.current = activeKpi;
  }, [activeKpi]);

  useEffect(() => {
    fetchMetrics();
    fetchSops('', 1, 10, { sortBy: '', sortDir: '' }, 'Total SOPs');
  }, [fetchMetrics, fetchSops]);

  // Periodic auto-refresh to keep data in sync with selected KPI
  useEffect(() => {
    const getIntervalMinutes = () => {
      const saved = sessionStorage.getItem('unifiedAlertsAutoRefresh');
      return saved ? Number(saved) : 5;
    };

    let intervalId: any = null;
    const startTimer = () => {
      if (intervalId) clearInterval(intervalId);
      const mins = getIntervalMinutes();
      if (mins > 0) {
        intervalId = setInterval(() => {
          fetchMetrics();
          fetchSops(search.trim(), page, pageSize, sortingInfo, activeKpiRef.current);
        }, mins * 60 * 1000);
      }
    };

    startTimer();

    const handleStorageChange = () => {
      startTimer();
    };
    window.addEventListener('storage', handleStorageChange);

    return () => {
      if (intervalId) clearInterval(intervalId);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [search, page, pageSize, sortingInfo, fetchMetrics, fetchSops]);

  // Debounced search
  useEffect(() => {
    if (!searchTouched) return;
    const val = search.trim();
    if (val.length > 0 && val.length < 3) return;
    const t = setTimeout(() => {
      setPage(1);
      fetchSops(val, 1, pageSize, sortingInfo, activeKpiRef.current);
    }, 500);
    return () => clearTimeout(t);
  }, [search, searchTouched, pageSize, sortingInfo, fetchSops]);

  // ── Add / Edit / Resubmit ──────────────────────────────────────────────────
  const openAdd = () => {
    setEditingId(null);
    setIsResubmit(false);
    setSopName('');
    setSopLink('');
    setSopApprover('');
    setSopApprovalRequired(true);
    setIsModalOpen(true);
  };
  const openEdit = (row: SopRow) => {
    setEditingId(row.id);
    setIsResubmit(false);
    setSopName(row.name);
    setSopLink(row.link ?? '');
    setSopApprover(row.assignedApprover ?? '');
    setSopApprovalRequired(row.approvalRequired !== undefined ? row.approvalRequired : true);
    setIsModalOpen(true);
  };
  const openResubmit = (row: SopRow) => {
    if (!isCreatorOfSop(row)) return;
    setEditingId(row.id);
    setIsResubmit(true);
    setSopName(row.name);
    setSopLink(row.link ?? '');
    setSopApprover(row.assignedApprover ?? '');
    setSopApprovalRequired(row.approvalRequired !== undefined ? row.approvalRequired : true);
    setIsModalOpen(true);
  };
  const closeModal = () => {
    if (actionLoading) return;
    setIsModalOpen(false);
    setSopName('');
    setSopLink('');
    setSopApprover('');
    setSopApprovalRequired(true);
    setEditingId(null);
    setIsResubmit(false);
  };

  const handleSaveModal = async ({
    sopName: name,
    sopLink: link,
    approvalRequired,
    owner,
  }: {
    sopName: string;
    sopLink: string;
    approvalRequired: boolean;
    owner: string;
  }) => {
    if (!name.trim() || !link.trim() || (approvalRequired && !owner.trim()) || actionLoading) return;
    setActionLoading(true);
    try {
      const approverVal = approvalRequired ? owner.trim() : null;
      const res = editingId
        ? await updateSop(editingId, name.trim(), link.trim(), sopType, approverVal, approvalRequired)
        : await createSop(name.trim(), link.trim(), sopType, approverVal, approvalRequired);

      if (res?.success === true) {
        showSnack(
          res?.data?.message ||
          res?.message ||
          (isResubmit
            ? `SOP "${name.trim()}" resubmitted successfully`
            : editingId
              ? 'SOP updated successfully'
              : 'SOP created successfully')
        );
        closeModal();
        await Promise.all([
          fetchMetrics(),
          fetchSops(search.trim(), 1, pageSize, sortingInfo, activeKpiRef.current),
        ]);
      } else {
        showSnack(
          res?.response?.data?.data?.message || res?.response?.data?.message || res?.message || 'Operation failed',
          'error'
        );
      }
    } catch {
      showSnack('Something went wrong', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  // ── Delete ─────────────────────────────────────────────────────────────────
  const handleDeleteClick = (row: SopRow) => {
    // if (!canDeleteSop(row)) return;
    setSopToDelete(row);
    setDeleteModalOpen(true);
  };
  const confirmDelete = async () => {
    if (!sopToDelete || deletingId) return;
    setDeleteModalOpen(false);
    setDeletingId(sopToDelete.id);
    try {
      const res = await deleteSop(sopToDelete.id);
      if (res?.response?.status || res?.status === 500 || res?.isAxiosError) {
        showSnack(res?.response?.data?.data?.message || res?.response?.data?.message || 'Failed to delete SOP', 'error');
      } else {
        showSnack(res?.data?.message || res?.message || 'SOP deleted successfully');
      }
      await Promise.all([
        fetchMetrics(),
        fetchSops(search.trim(), 1, pageSize, sortingInfo, activeKpiRef.current),
      ]);
    } catch {
      showSnack('Failed to delete SOP', 'error');
    } finally {
      setDeletingId(null);
      setSopToDelete(null);
    }
  };

  // ── Approve / Reject Handlers ──────────────────────────────────────────────
  const handleOpenApprovalModal = (sop: SopRow, type: 'approve' | 'reject') => {
    setApprovalModalState({ open: true, type, sop });
  };

  const handleCloseApprovalModal = () => {
    if (approvalLoading) return;
    setApprovalModalState((prev) => ({ ...prev, open: false }));
  };

  const handleApprovalSubmit = async (comments: string) => {
    const sop = approvalModalState.sop;
    if (!sop) return;
    setApprovalLoading(true);
    try {
      const isApprove = approvalModalState.type === 'approve';
      const res = isApprove
        ? await approveSop(sop.id, comments)
        : await rejectSop(sop.id, comments);

      if (res?.success === true) {
        showSnack(res?.message || (isApprove ? `SOP "${sop.name}" approved successfully` : `SOP "${sop.name}" has been rejected`));
        handleCloseApprovalModal();
        await Promise.all([
          fetchMetrics(),
          fetchSops(search.trim(), page, pageSize, sortingInfo, activeKpiRef.current),
        ]);
      } else {
        const errMsg =
          res?.response?.data?.data?.message ||
          res?.response?.data?.message ||
          res?.message ||
          'Action failed';
        showSnack(errMsg, 'error');
      }
    } catch {
      showSnack('Something went wrong', 'error');
    } finally {
      setApprovalLoading(false);
    }
  };

  // ── Columns ────────────────────────────────────────────────────────────────
  const sopColumns = useMemo(() => [
    {
      key: 'sopName',
      name: 'SOP NAME',
      width: autoColumnWidth(220, 1.8),
      formatter: ({ row }: { row: SopRow }) => (
        <Typography
          sx={{
            color: colors.buttons.primary.color,
            fontWeight: 600,
            textDecoration: row.link ? 'underline' : 'none',
            cursor: row.link ? 'pointer' : 'default',
            fontSize: '0.85rem',
          }}
          onClick={row.link ? () => window.open(row.link, '_blank') : undefined}
        >
          {row.sopName || row.name}
        </Typography>
      ),
    },
    {
      key: 'assignedApprover',
      name: 'OWNER',
      width: autoColumnWidth(220, 1.4),
      formatter: ({ row }: { row: SopRow }) => {
        const approver = (row.assignedApprover || row.owner || '').trim();
        const isYou = Boolean(currentUserEmail && approver && approver.toLowerCase() === currentUserEmail);
        const display = approver ? (isYou ? `${approver} (You)` : approver) : '-';
        return (
          <Typography
            sx={{
              fontSize: '0.85rem',
              fontWeight: isYou ? 600 : 400,
              color: isYou ? colors.buttons.primary.color : '#1E293B',
            }}
          >
            {display}
          </Typography>
        );
      },
    },
    {
      key: 'version',
      name: 'VERSION',
      width: autoColumnWidth(100),
      formatter: ({ row }: { row: SopRow }) => (
        <Typography sx={{ fontSize: '0.85rem' }}>
          {row.version ? `v${row.version}` : '-'}
        </Typography>
      ),
    },
    {
      key: 'comments',
      name: 'COMMENTS',
      width: autoColumnWidth(160, 1),
      formatter: ({ row }: { row: SopRow }) => (
        <Typography sx={{ fontSize: '0.85rem', color: row.comments ? '#1E293B' : '#94A3B8' }}>
          {row.comments || '-'}
        </Typography>
      ),
    },
    {
      key: 'status',
      name: 'STATUS',
      width: autoColumnWidth(170),
      formatter: ({ row }: { row: SopRow }) => {
        const status = row.status || '-';
        let bg = 'transparent';
        let textColor = '#1E293B';
        if (/approved/i.test(status)) {
          bg = '#16A34A';
          textColor = '#FFFFFF';
        } else if (/rejected/i.test(status)) {
          bg = '#DC2626';
          textColor = '#FFFFFF';
        } else if (/pending/i.test(status)) {
          bg = '#F59E0B';
          textColor = '#FFFFFF';
        }

        if (bg !== 'transparent') {
          return (
            <Box
              sx={{
                display: 'inline-block',
                bgcolor: bg,
                color: textColor,
                px: 1.5,
                py: 0.5,
                borderRadius: '4px',
                fontSize: '10px',
                lineHeight: '20px',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
              }}
            >
              {status}
            </Box>
          );
        }

        return <Typography sx={{ fontSize: '0.85rem' }}>{status}</Typography>;
      },
    },
    {
      key: 'createdByEmail',
      name: 'CREATED BY',
      width: autoColumnWidth(200, 1.2),
      formatter: ({ row }: { row: SopRow }) => (
        <Typography sx={{ fontSize: '0.85rem' }}>{row.createdByEmail || row.createdBy || '-'}</Typography>
      ),
    },
    {
      key: 'updatedByEmail',
      name: 'UPDATED BY',
      width: autoColumnWidth(200, 1.2),
      formatter: ({ row }: { row: SopRow }) => (
        <Typography sx={{ fontSize: '0.85rem' }}>{row.updatedByEmail || row.updatedBy || '-'}</Typography>
      ),
    },
    {
      key: 'updatedAt',
      name: 'LAST UPDATED',
      width: autoColumnWidth(170, 1.1),
      formatter: ({ row }: { row: SopRow }) => (
        <Typography sx={{ fontSize: '0.85rem' }}>
          {row.lastUpdated || formatLastUpdated(row.updatedAt)}
        </Typography>
      ),
    },
    {
      key: 'actions',
      name: 'ACTIONS',
      width: autoColumnWidth(150),
      cellClass: 'manage-sop-actions-cell',
      formatter: ({ row }: { row: SopRow }) => {
        const approver = row.assignedApprover?.trim() || '';
        const isYou = Boolean(currentUserEmail && approver && approver.toLowerCase() === currentUserEmail);
        const isPending = /pending/i.test(row.status || '');

        if (isYou && isPending) {
          return (
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
              <Typography
                onClick={() => handleOpenApprovalModal(row, 'approve')}
                sx={{
                  color: colors.buttons.primary.color,
                  fontWeight: 600,
                  fontSize: '0.82rem',
                  cursor: 'pointer',
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                APPROVE
              </Typography>
              <Typography
                onClick={() => handleOpenApprovalModal(row, 'reject')}
                sx={{
                  color: '#DC2626',
                  fontWeight: 600,
                  fontSize: '0.82rem',
                  cursor: 'pointer',
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                REJECT
              </Typography>
            </Box>
          );
        }

        const isRejected = /rejected/i.test(row.status || '');
        const isApproved = /approved/i.test(row.status || '');
        const isCreator = isCreatorOfSop(row);
        const canDelete = canDeleteSop(row);

        return (
          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
            {isRejected ? (
              isCreator ? (
                <Typography
                  onClick={() => {
                    if (loading || actionLoading || deletingId === row.id) return;
                    openResubmit(row);
                  }}
                  sx={{
                    color: colors.buttons.primary.color,
                    fontWeight: 600,
                    fontSize: '0.82rem',
                    cursor: loading || actionLoading || deletingId === row.id ? 'not-allowed' : 'pointer',
                    opacity: loading || actionLoading || deletingId === row.id ? 0.6 : 1,
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em',
                    mr: 1,
                    '&:hover': {
                      textDecoration: loading || actionLoading || deletingId === row.id ? 'none' : 'underline',
                    },
                  }}
                >
                  RESUBMIT
                </Typography>
              ) : null
            ) : !isApproved && isCreator ? (
              <Tooltip title="Edit SOP" arrow>
                <span>
                  <IconButton
                    size="small"
                    disabled={loading || actionLoading || deletingId === row.id}
                    sx={{ color: colors.buttons.primary.color }}
                    onClick={() => openEdit(row)}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            ) : null}
            {canDelete ? (
              <Tooltip title="Delete SOP" arrow>
                <span>
                  <IconButton
                    size="small"
                    disabled={loading || actionLoading || deletingId === row.id}
                    sx={{ color: (colors.buttons?.primary as any)?.deleteButtonBg || '#D32F2F' }}
                    onClick={() => handleDeleteClick(row)}
                  >
                    {deletingId === row.id
                      ? <CircularProgress size={16} />
                      : <DeleteIcon fontSize="small" />}
                  </IconButton>
                </span>
              </Tooltip>
            ) : null} 
          </Box>
        );
      },
    },
  ], [loading, actionLoading, deletingId, colors, currentUserEmail, isCreatorOfSop, canDeleteSop, openEdit, openResubmit]);

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <PageWrapper sx={{ minHeight: '100vh', mt: { xs: '78px', lg: '60px' }  }}>

      {/* Header row: breadcrumb + ADD NEW SOP */}
      <Box
        sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          flexWrap: 'wrap', gap: 1, mb: 2.5,
        }}
      >
        {/* Breadcrumb */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Typography
            onClick={handleBack}
            sx={{
              fontSize: '0.85rem', fontWeight: 500,
              color: colors.buttons.primary.color, cursor: 'pointer',
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            {sopType === 'OPS_PULSE' ? 'Ops Pulse' : 'Summary'}
          </Typography>
          <ArrowBackIosNewIcon sx={{ fontSize: '0.6rem', color: '#64748B', transform: 'rotate(180deg)' }} />
          <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#1E293B' }}>
            {`Manage SOP - ${sopType === 'OPS_PULSE' ? 'Ops Pulse' : 'EWS'}`}
          </Typography>
        </Box>

        <Button
          variant="outlined"
          onClick={openAdd}
          sx={{
            fontSize: '0.82rem', fontWeight: 600, textTransform: 'uppercase',
            letterSpacing: '0.05em', px: 3, height: 38, borderRadius: '6px',
            borderColor: `${colors.buttons.primary.borderedBorder} !important`,
            color: `${colors.buttons.primary.borderedText} !important`,
            '&:hover': { borderColor: `${colors.buttons.primary.borderedBorder} !important` },
          }}
        >
          ADD NEW SOP
        </Button>
      </Box>

      {/* KPI cards */}
      <Box sx={{ display: 'flex', gap: 1.5, mb: 3, flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
        {[
          { key: 'Total SOPs', label: 'Total SOPs', value: metricsLoading ? '-' : metrics.totalSOPs, loading: metricsLoading, tooltip: 'Total SOPs' },
          { key: 'Approval Pending', label: 'Approval Pending', value: metricsLoading ? '-' : metrics.pendingApproval, loading: metricsLoading, tooltip: 'Approval Pending' },
          { key: 'Rejected', label: 'Rejected', value: metricsLoading ? '-' : metrics.rejected, loading: metricsLoading, tooltip: 'Rejected' },
          { key: 'Approved', label: 'Approved', value: metricsLoading ? '-' : metrics.approved, loading: metricsLoading, tooltip: 'Approved' },
        ].map((tile) => {
          const isSelected = activeKpi === tile.key;
          return (
            <Box key={tile.key} sx={{ flex: 1, minWidth: 0 }}>
              <NumberWidget
                title={tile.label}
                titleKey={tile.key}
                number={tile.value}
                isLoading={tile.loading}
                hasTooltip={true}
                tooltipLabel={tile.tooltip}
                renderTooltip={(label) => renderTooltipIcon(label, isSelected ? 'light' : 'dark')}
                bgColor={isSelected ? '#FFFFFF' : '#103771'}
                color={isSelected ? '#103771' : '#FFFFFF'}
                handleEvent={() => handleKpiClick(tile.key)}
                selectedTitle={activeKpi}
                minTitleHeight="24px"
              />
            </Box>
          );
        })}
      </Box>

      {/* Table card */}
      <Box
        sx={{
          backgroundColor: '#FFFFFF',
          borderRadius: '8px',
          border: '1px solid #E5ECF6',
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
          p: 2.5,
          overflow: 'hidden',
          '& .manage-sop-actions-cell': {
            display: 'flex !important',
            alignItems: 'center !important',
          },
          '& .rdg-row .rdg-cell.manage-sop-actions-cell': {
            display: 'flex !important',
            alignItems: 'center !important',
          },
          '& .rdg-row .rdg-cell:last-child': {
            display: 'flex !important',
            alignItems: 'center !important',
          },
        }}
      >
        <CustomizedDataGrid
          key="manage-sop-page-grid"
          title="Manage SOPs"
          isChildren={true}
          childrenContainerProps={{ sx: { my: 0, alignItems: 'center' } }}
          children={
            <TextField
              placeholder="Search SOPs"
              size="small"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setSearchTouched(true); }}
              sx={{ ...inputSx, width: 260 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: `${colors.inputNew.inputPlaceholder} !important`, fontSize: 18 }} />
                  </InputAdornment>
                ),
              }}
              inputProps={{ style: { color: colors.inputNew.inputText, caretColor: colors.inputNew.inputText } }}
            />
          }
          tableColumns={sopColumns}
          tableData={sops}
          tableType="ManageSopPage"
          defaultColumnNames={['SOP NAME', 'OWNER', 'VERSION', 'COMMENTS', 'STATUS', 'CREATED BY', 'UPDATED BY', 'LAST UPDATED', 'ACTIONS']}
          listLength={totalDocs}
          page={page}
          pageSize={pageSize}
          paginationData={{ totalElements: totalDocs, totalPages }}
          onSorting={(info: any) => {
            setSortingInfo(info);
            fetchSops(search.trim(), page, pageSize, info, activeKpiRef.current);
          }}
          currSorting={sortingInfo}
          currFilter={currFilter}
          onFilterCheck={(filterInfo: any) => {
            setCurrFilter(filterInfo || {});
          }}
          handlePageChange={(_: any, p: number) => {
            setPage(p);
            fetchSops(search.trim(), p, pageSize, sortingInfo, activeKpiRef.current);
          }}
          handlepageSizeChange={(e: any) => {
            const newSize = Number.parseInt(e.target.value);
            setPageSize(newSize);
            setPage(1);
            fetchSops(search.trim(), 1, newSize, sortingInfo, activeKpiRef.current);
          }}
          isFetching={loading}
          isDateRangeFilter={false}
          isDisabled={false}
          isShowMoreButton={true}
          pageSizeOptions={[10, 50, 100, 200, 500, 1000]}
          isGroupBy={false}
          isSortIcon={true}
          rowHeight={40}
        />
      </Box>

      {/* Add / Edit / Resubmit Modal */}
      <AddEditSopModal
        open={isModalOpen}
        onClose={closeModal}
        editingId={editingId}
        isResubmit={isResubmit}
        initialName={sopName}
        initialLink={sopLink}
        initialOwner={sopApprover}
        initialApprovalRequired={sopApprovalRequired}
        loading={actionLoading}
        onSave={handleSaveModal}
      />

      {/* Delete Confirmation */}
      <DeleteConfirmationModal
        open={deleteModalOpen}
        onClose={() => { setDeleteModalOpen(false); setSopToDelete(null); }}
        onConfirm={confirmDelete}
        type={"SOP"}
        identifierName={sopToDelete?.name}
      />

      {/* Approve / Reject Modal */}
      <ApproveRejectSopModal
        open={approvalModalState.open}
        type={approvalModalState.type}
        sopName={approvalModalState.sop?.name || ''}
        loading={approvalLoading}
        onClose={handleCloseApprovalModal}
        onSubmit={handleApprovalSubmit}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={{
          top: '72px !important',
          right: '32px !important',
          zIndex: 99999,
        }}
      >
        <Box sx={{ width: '100%', maxWidth: '480px' }}>
          <PepwiseToast
            type={snackbar.severity}
            title={snackbar.title}
            message={snackbar.message}
            onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          />
        </Box>
      </Snackbar>
    </PageWrapper>
  );
};

export default ManageSOPPage;
